import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabase/supabase';
import {
  FaSearch,
  FaPlus,
  FaWallet,
  FaComments,
  FaCog,
  FaHome,
  FaEllipsisH,
  FaTh,         // grid
  FaSlidersH,   // sliders
  FaThList,     // masonry view
  FaCompass,    // navigation
} from 'react-icons/fa';
import iwintologo from '../../assets/svgs/logo circular2.png';
import './FloatingSidebarButton.css';

const CLICK_DRAG_THRESHOLD_PX = 6;
const MOBILE_DRAG_THRESHOLD_PX = 12; // Higher threshold for mobile devices

/** Layout constants that MATCH the CSS below */
const BUTTON_SIZE = 80;   // .floating-sidebar-button size
const MENU_LEFT = 0;      // .floating-menu { left: 0px; }
const MENU_TOP = -90;     // .floating-menu { top: -90px; }
const ORIGIN_LEFT = 0;    // .menu-items-container { left: 0px; }
const ORIGIN_TOP = 90;    // .menu-items-container { top: 90px; }
const ITEM_SIZE = 50;     // .floating-menu-item { width/height: 50px; }
const ITEM_PADDING = 6;   // safety padding
const MORE_GAP = 60;      // horizontal gap between the 3-dots and the 2 inline buttons
const FLIP_INSET_PX = 70; // bring flipped arc a bit closer to the logo

const FloatingSidebarButton = ({ userProfile, onFilterChange, onViewChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [isFilterDropdownFading, setIsFilterDropdownFading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(() => {
    const saved = localStorage.getItem('feed_selected_filter');
    return saved || 'All';
  });
  const filterDropdownTimeoutRef = useRef(null);

  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [isViewDropdownFading, setIsViewDropdownFading] = useState(false);
  const [selectedView, setSelectedView] = useState(() => {
    const saved = localStorage.getItem('feed_selected_view');
    return saved || 'Grid';
  });
  const viewDropdownTimeoutRef = useRef(null);

  const [showNavigationDropdown, setShowNavigationDropdown] = useState(false);
  const [isNavigationDropdownFading, setIsNavigationDropdownFading] = useState(false);
  const navigationDropdownTimeoutRef = useRef(null);
  const [openToLeft, setOpenToLeft] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const dockedPosRef = useRef(null);

  const positionRef = useRef({
    x: 0,
    y: 100, // Initial default: top-left but a bit down
  });
  const [position, setPosition] = useState(positionRef.current);
  const pendingPosRef = useRef(positionRef.current);

  // dragging
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const startPointerRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const rafRef = useRef(null);
  const isMobileRef = useRef(false);

  // Detect if device is mobile/touch
  useEffect(() => {
    const checkMobile = () => {
      isMobileRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { translations: tRaw } = useLanguage();
  const { userProfile: authUserProfile } = useAuth();
  const { currentUser } = useAuth();
  const t = { ...tRaw, feed: { ...tRaw?.feed } };

  // --- Chat unread badge (counts unread conversations, not messages) ---
  const [unreadChatConversationsCount, setUnreadChatConversationsCount] = useState(0);
  const chatUnreadConvIdsRef = useRef(new Set());
  const chatConversationIdsRef = useRef(new Set());
  const chatBadgeCleanupRef = useRef(null);

  const computeUnreadChatConversations = useCallback(async () => {
    if (!currentUser?.id) {
      chatUnreadConvIdsRef.current = new Set();
      chatConversationIdsRef.current = new Set();
      setUnreadChatConversationsCount(0);
      return;
    }

    // 1) conversations for this user
    const { data: parts, error: pErr } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', currentUser.id);
    if (pErr) throw pErr;

    const convIds = new Set((parts || []).map((p) => p.conversation_id).filter(Boolean));
    chatConversationIdsRef.current = convIds;

    if (convIds.size === 0) {
      chatUnreadConvIdsRef.current = new Set();
      setUnreadChatConversationsCount(0);
      return;
    }

    const convIdList = [...convIds];

    // 2) all messages in those convs not sent by me
    const { data: msgs, error: mErr } = await supabase
      .from('messages')
      .select('id, conversation_id')
      .in('conversation_id', convIdList)
      .neq('sender_id', currentUser.id);
    if (mErr) throw mErr;

    if (!msgs?.length) {
      chatUnreadConvIdsRef.current = new Set();
      setUnreadChatConversationsCount(0);
      return;
    }

    const msgIds = msgs.map((m) => m.id);

    // 3) read receipts
    const { data: reads, error: rErr } = await supabase
      .from('message_reads')
      .select('message_id')
      .eq('user_id', currentUser.id)
      .in('message_id', msgIds);
    if (rErr) throw rErr;

    const readSet = new Set((reads || []).map((r) => r.message_id));
    const unreadConvIds = new Set();
    for (const m of msgs) {
      if (!readSet.has(m.id)) unreadConvIds.add(m.conversation_id);
    }

    chatUnreadConvIdsRef.current = unreadConvIds;
    setUnreadChatConversationsCount(unreadConvIds.size);
  }, [currentUser?.id]);

  // realtime: bump unread count when a new message arrives in a conversation we haven't opened/read
  const subscribeChatBadge = useCallback(() => {
    if (!currentUser?.id) return () => {};

    // cleanup previous
    try { chatBadgeCleanupRef.current?.(); } catch (_) {}
    chatBadgeCleanupRef.current = null;

    const channel = supabase
      .channel(`chat_badge_${currentUser.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload) => {
          const msg = payload.new;
          if (!msg?.conversation_id) return;
          if (msg.sender_id === currentUser.id) return;

          const convIds = chatConversationIdsRef.current;
          if (!convIds.has(msg.conversation_id)) {
            // Could be a brand new conversation created by someone else.
            // Refresh convIds + unread set, then try again.
            try {
              await computeUnreadChatConversations();
            } catch (_) {}

            const convIdsAfter = chatConversationIdsRef.current;
            if (!convIdsAfter.has(msg.conversation_id)) return;
          }

          // If user is currently viewing this conversation, don't count it as unread
          try {
            const active = localStorage.getItem('active_chat_conversation_id');
            // Only treat it as "active" while the user is actually on the /chat page
            if (location.pathname === '/chat' && active && active === msg.conversation_id) return;
          } catch (_) {}

          const unreadSet = chatUnreadConvIdsRef.current;
          if (unreadSet.has(msg.conversation_id)) return;

          const next = new Set(unreadSet);
          next.add(msg.conversation_id);
          chatUnreadConvIdsRef.current = next;
          setUnreadChatConversationsCount(next.size);
        }
      )
      .subscribe();

    const cleanup = () => {
      try { supabase.removeChannel(channel); } catch (_) {}
    };
    chatBadgeCleanupRef.current = cleanup;
    return cleanup;
  }, [computeUnreadChatConversations, currentUser?.id, location.pathname]);

  // listen to chat read + conversation created events from Chat page
  useEffect(() => {
    if (!currentUser?.id) return;

    const onRead = (e) => {
      const conversationId = e?.detail?.conversationId;
      if (!conversationId) return;
      const unreadSet = chatUnreadConvIdsRef.current;
      if (!unreadSet.has(conversationId)) return;
      const next = new Set(unreadSet);
      next.delete(conversationId);
      chatUnreadConvIdsRef.current = next;
      setUnreadChatConversationsCount(next.size);
    };

    const onCreated = () => {
      // refresh conversation id set (and unread count) when new chat is created
      computeUnreadChatConversations().catch(() => {});
    };

    window.addEventListener('chat:read', onRead);
    window.addEventListener('chat:conversationCreated', onCreated);
    return () => {
      window.removeEventListener('chat:read', onRead);
      window.removeEventListener('chat:conversationCreated', onCreated);
    };
  }, [computeUnreadChatConversations, currentUser?.id]);

  // initial load + subscription
  useEffect(() => {
    if (!currentUser?.id) return;
    let cleanup = null;

    computeUnreadChatConversations()
      .then(() => { cleanup = subscribeChatBadge(); })
      .catch(() => { cleanup = subscribeChatBadge(); });

    return () => {
      try { cleanup?.(); } catch (_) {}
    };
  }, [computeUnreadChatConversations, currentUser?.id, subscribeChatBadge]);

  const handleMenuClick = useCallback((route) => {
    setIsOpen(false);
    setShowMore(false);
    if (route) navigate(route);
  }, [navigate]);

  const handleMoreToggle = useCallback(() => {
    // keep menu open, just toggle the two inline buttons
    setShowMore((v) => !v);
  }, []);

  // ----- Menu items on the arc -----
  const arcItems = useMemo(() => [
    {
      kind: 'avatar',
      label: t.feed?.profile || 'Profile',
      onClick: () => handleMenuClick('/profile'),
      className: 'floating-menu-item-profile-img',
      route: '/profile',
    },
    {
      kind: 'icon',
      label: t.feed?.home || 'Home',
      onClick: () => handleMenuClick('/feed'),
      className: 'floating-menu-item-home',
      icon: <FaHome />,
      route: '/feed',
    },
    {
      kind: 'icon',
      label: t.feed?.search || 'Search',
      onClick: () => handleMenuClick('/search'),
      className: 'floating-menu-item-search',
      icon: <FaSearch />,
      route: '/search',
    },
    {
      kind: 'icon',
      label: t.feed?.create || 'Create',
      onClick: () => handleMenuClick('/create'),
      className: 'floating-menu-item-create',
      icon: <FaPlus />,
      route: '/create',
    },
    {
      kind: 'icon',
      label: t.feed?.wallet || 'Wallet',
      onClick: () => handleMenuClick('/wallet'),
      className: 'floating-menu-item-wallet',
      icon: <FaWallet />,
      route: '/wallet',
    },
    {
      kind: 'icon',
      label: t.feed?.chat || 'Chat',
      onClick: () => handleMenuClick('/chat'),
      className: 'floating-menu-item-chat',
      icon: <FaComments />,
      route: '/chat',
    },
    {
      kind: 'icon',
      label: t.feed?.settings || 'Settings',
      onClick: () => handleMenuClick('/settings'),
      className: 'floating-menu-item-settings',
      icon: <FaCog />,
      route: '/settings',
    },
    {
      kind: 'icon',
      label: t.feed?.more || 'More',
      onClick: () => handleMoreToggle(),
      className: 'floating-menu-item-more',
      icon: <FaEllipsisH />,
      route: null, // stays in place, toggles inline options
    },
  ], [t.feed, handleMenuClick, handleMoreToggle]);

  // Sync initial values with Feed.jsx on mount
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(selectedFilter);
    }
    if (onViewChange) {
      onViewChange(selectedView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - sync initial localStorage values

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setShowMore(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);


  // Arc layout
  const total = arcItems.length;
  const radius = 150;
  const startDeg = -75;
  const endDeg = 75;

  // Precompute positions for all arc items (so we can reuse the "More" position)
  const positions = useMemo(() => {
    const arr = [];
    for (let i = 0; i < total; i++) {
      const tNorm = total === 1 ? 0.5 : i / (total - 1);
      const angleDeg = startDeg + (endDeg - startDeg) * tNorm;
      const theta = (angleDeg * Math.PI) / 180;
      arr.push({ x: Math.cos(theta) * radius, y: Math.sin(theta) * radius });
    }
    return arr;
  }, [total, radius, startDeg, endDeg]);

  // Flip arc horizontally when opening to the left
  const orientedPositions = useMemo(() => {
    if (!openToLeft) return positions;
    // When flipped, move arc slightly right so it's closer to the logo
    return positions.map(p => ({ x: -p.x + FLIP_INSET_PX, y: p.y }));
  }, [positions, openToLeft]);

  const moreIndex = useMemo(
    () => arcItems.findIndex((it) => it.className?.includes('floating-menu-item-more')),
    [arcItems]
  );
  const morePos = orientedPositions[moreIndex] || { x: 0, y: 0 };

  // Inline buttons gap direction (right by default, left when flipped)
  const INLINE_GAP = openToLeft ? -MORE_GAP : MORE_GAP;

  // ---- Bounds calculation so items never go off-screen ----
  const computeArcExtents = useCallback(() => {
    // Arc items extents based on current orientation
    const xs = orientedPositions.map(p => p.x);
    const ys = orientedPositions.map(p => p.y);

    // If "More" submenu is open, include the inline buttons in the correct direction
    if (showMore && moreIndex >= 0) {
      if (location.pathname === '/feed') {
        // On /feed route: show view, filter, and navigation buttons (3 buttons total)
        xs.push(morePos.x + INLINE_GAP, morePos.x + INLINE_GAP * 2, morePos.x + INLINE_GAP * 3);
        ys.push(morePos.y, morePos.y, morePos.y);
      } else {
        // On other routes: only show navigation button (1 button total)
        xs.push(morePos.x + INLINE_GAP);
        ys.push(morePos.y);
      }
    }

    const minX = Math.min(...xs) - ITEM_PADDING;
    const maxX = Math.max(...xs) + ITEM_PADDING;
    const minY = Math.min(...ys) - ITEM_PADDING;
    const maxY = Math.max(...ys) + ITEM_PADDING;
    return { minX, maxX, minY, maxY };
  }, [orientedPositions, INLINE_GAP, showMore, moreIndex, morePos.x, morePos.y, location.pathname]);

  // Given viewport size, derive allowable button (x,y)
  const computeDragLimits = useCallback((vw, vh) => {
    if (isOpen) {
      // STRICT limits when open: prevent menu items from going off-screen
      const { minX, maxX, minY, maxY } = computeArcExtents();

      // Absolute positioning math (button top-left + menu/top + origin + item translate)
      const offsetX = MENU_LEFT + ORIGIN_LEFT;
      const offsetY = MENU_TOP + ORIGIN_TOP;

      const minPosXFromItems = -(offsetX + minX);
      const maxPosXFromItems = vw - (offsetX + maxX + ITEM_SIZE);

      const minPosYFromItems = -(offsetY + minY);
      const maxPosYFromItems = vh - (offsetY + maxY + ITEM_SIZE);

      // Keep the button fully visible too
      const minPosXFromButton = 0;
      const maxPosXFromButton = vw - BUTTON_SIZE;

      const minPosYFromButton = 0;
      const maxPosYFromButton = vh - BUTTON_SIZE;

      return {
        minX: Math.max(minPosXFromItems, minPosXFromButton),
        maxX: Math.min(maxPosXFromItems, maxPosXFromButton),
        minY: Math.max(minPosYFromItems, minPosYFromButton),
        maxY: Math.min(maxPosYFromItems, maxPosYFromButton),
      };
    } else {
      // Relaxed limits when closed: user can drag button anywhere as long as it stays on screen
      return {
        minX: 0,
        maxX: vw - BUTTON_SIZE,
        minY: 0,
        maxY: vh - BUTTON_SIZE,
      };
    }
  }, [isOpen, computeArcExtents]);

  // rAF apply pending pos
  const applyPendingPos = useCallback(() => {
    rafRef.current = null;
    positionRef.current = pendingPosRef.current;
    setPosition(positionRef.current);
  }, []);

  const requestApply = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(applyPendingPos);
  }, [applyPendingPos]);

  // pointer handlers (drag)
  const handlePointerDown = (e) => {
    if (e.button !== 0) return;

    // Reset docked position if user starts dragging manually
    dockedPosRef.current = null;

    const rect = buttonRef.current.getBoundingClientRect();

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;

    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    startPointerRef.current = { x: clientX, y: clientY };
    movedRef.current = false;
    isDraggingRef.current = true;
    setIsDragging(true);

    // Use both pointer and touch events for better mobile support
    document.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.addEventListener('pointerup', handlePointerUp, { passive: true });
    document.addEventListener('touchmove', handlePointerMove, { passive: true });
    document.addEventListener('touchend', handlePointerUp, { passive: true });

    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;

    const tentativeX = clientX - dragOffsetRef.current.x;
    const tentativeY = clientY - dragOffsetRef.current.y;

    const { minX, maxX, minY, maxY } = computeDragLimits(window.innerWidth, window.innerHeight);

    const clampedX = Math.max(minX, Math.min(tentativeX, maxX));
    const clampedY = Math.max(minY, Math.min(tentativeY, maxY));

    const dx = clientX - startPointerRef.current.x;
    const dy = clientY - startPointerRef.current.y;

    // Use different thresholds for mobile vs desktop
    const threshold = isMobileRef.current ? MOBILE_DRAG_THRESHOLD_PX : CLICK_DRAG_THRESHOLD_PX;
    if (!movedRef.current && (dx * dx + dy * dy) > threshold * threshold) {
      movedRef.current = true;
    }

    pendingPosRef.current = { x: clampedX, y: clampedY };
    requestApply();
  };

  const handlePointerUp = () => {
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    document.removeEventListener('touchmove', handlePointerMove);
    document.removeEventListener('touchend', handlePointerUp);
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const handleToggle = () => {
    if (isAnimating) return;
    if (isDraggingRef.current || movedRef.current) return;

    if (isOpen) {
      // Closing
      setIsOpen(false);
      setShowMore(false);
      // Move back to docked position if one exists
      if (dockedPosRef.current) {
        setIsAnimating(true);
        setIsMoving(true); // Enable slow transition
        setPosition(dockedPosRef.current);
        positionRef.current = dockedPosRef.current; // sync ref
        dockedPosRef.current = null;
        setTimeout(() => {
          setIsMoving(false);
          setIsAnimating(false);
        }, 400);
      }
    } else {
      // Opening
      setIsAnimating(true);

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { x: curX, y: curY } = positionRef.current;

      // Calculate Safe Position logic
      // We want to ensure ~180px clearance from top/bottom and ~180px from side for the menu
      const SAFE_MARGIN_Y = 180;
      const SAFE_MARGIN_X_RIGHT = 180; // Clearance needed if opening right
      const SAFE_MARGIN_X_LEFT = 100;  // Clearance needed if opening left

      let targetX = curX;
      let targetY = curY;

      // Y-axis Adjustment
      if (curY < SAFE_MARGIN_Y) {
        targetY = SAFE_MARGIN_Y;
      } else if (vh - curY < SAFE_MARGIN_Y + BUTTON_SIZE) {
        targetY = vh - SAFE_MARGIN_Y - BUTTON_SIZE;
      }

      // Decide Left/Right Opening based on Screen Center
      // If left of center -> open Right. If right of center -> open Left.
      const isLeftHalf = (curX + BUTTON_SIZE / 2) < (vw / 2);
      const willOpenLeft = !isLeftHalf;

      setOpenToLeft(willOpenLeft);

      // X-axis Adjustment if needed (though usually side-edge is fine if we flip)
      // If opening Right (isLeftHalf), ensure right space
      if (!willOpenLeft) {
        if (vw - curX < SAFE_MARGIN_X_RIGHT) {
          // rare case: very narrow screen
          targetX = vw - SAFE_MARGIN_X_RIGHT;
        }
      } else {
        // Opening Left
        if (curX < SAFE_MARGIN_X_LEFT) {
          targetX = SAFE_MARGIN_X_LEFT;
        }
      }

      // Check if we need to move
      if (Math.abs(targetX - curX) > 5 || Math.abs(targetY - curY) > 5) {
        // We are moving
        dockedPosRef.current = { x: curX, y: curY };
        setIsMoving(true);
        const newPos = { x: targetX, y: targetY };
        setPosition(newPos);
        positionRef.current = newPos;

        // Wait for move to start/finish before showing menu?
        // User wants "move smoothly ... and display". Simultaneous or staggered.
        // Let's pop menu immediately as it transitions
        setIsOpen(true);

        setTimeout(() => {
          setIsAnimating(false);
          // Keep isMoving true purely for the transition duration is risky if they drag immediately
          // but we block toggling if animating.
          setTimeout(() => setIsMoving(false), 100);
        }, 400);
      } else {
        // Safe position already
        setIsOpen(true);
        setTimeout(() => setIsAnimating(false), 300);
      }
    }
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    localStorage.setItem('feed_selected_filter', filter);
    if (onFilterChange) {
      onFilterChange(filter);
    }
    // Hide the filter and view buttons when a filter is selected
    setShowMore(false);
    setShowFilterDropdown(false);
    setShowViewDropdown(false);
    setShowNavigationDropdown(false);
  };

  const handleViewSelect = (view) => {
    setSelectedView(view);
    localStorage.setItem('feed_selected_view', view);
    if (onViewChange) {
      onViewChange(view);
    }
    // Hide the filter and view buttons when a view is selected
    setShowMore(false);
    setShowFilterDropdown(false);
    setShowViewDropdown(false);
    setShowNavigationDropdown(false);
    console.log('View changed to:', view);
  };

  const handleNavigationSelect = (route) => {
    // Hide the dropdowns when navigation is selected
    setShowMore(false);
    setShowFilterDropdown(false);
    setShowViewDropdown(false);
    setShowNavigationDropdown(false);
    // Navigate to the selected route
    navigate(route);
  };

  // Keep within viewport after resize or when "showMore" changes
  useEffect(() => {
    const onResizeOrChange = () => {
      const { minX, maxX, minY, maxY } = computeDragLimits(window.innerWidth, window.innerHeight);
      const { x, y } = positionRef.current;
      const clampedX = Math.max(minX, Math.min(x, maxX));
      const clampedY = Math.max(minY, Math.min(y, maxY));
      pendingPosRef.current = { x: clampedX, y: clampedY };
      requestApply();
    };
    window.addEventListener('resize', onResizeOrChange);
    onResizeOrChange(); // also clamp when showMore toggles
    return () => window.removeEventListener('resize', onResizeOrChange);
  }, [computeDragLimits, showMore, requestApply]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (filterDropdownTimeoutRef.current) {
        clearTimeout(filterDropdownTimeoutRef.current);
      }
      if (viewDropdownTimeoutRef.current) {
        clearTimeout(viewDropdownTimeoutRef.current);
      }
      if (navigationDropdownTimeoutRef.current) {
        clearTimeout(navigationDropdownTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`floating-sidebar-container ${isDragging ? 'dragging' : ''} ${isMoving ? 'smooth-move' : ''}`}
      style={{ transform: `translate3d(${position.x}px, ${position.y}px, 0)` }}
    >
      {/* Main Button */}
      <button
        ref={buttonRef}
        className={`floating-sidebar-button ${isOpen ? 'open' : ''}`}
        onPointerDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onClick={handleToggle}
        aria-label="Toggle menu"
        draggable={false}
        style={{ touchAction: 'none' }}
      >
        <img src={iwintologo} alt="iWinto" className="floating-logo" draggable={false} />
      </button>

      {/* Half Circle Menu */}
      <div ref={menuRef} className={`floating-menu ${isOpen ? 'open' : ''}`}>
        {/* Arc origin */}
        <div className="menu-items-container">
          {arcItems.map((item, i) => {
            const { x, y } = orientedPositions[i];
            const isActive = item.route && location.pathname === item.route;

            if (item.kind === 'avatar') {
              return (
                <button
                  key={`avatar-${i}`}
                  className={`floating-menu-item ${item.className} ${isActive ? 'active' : ''}`}
                  onClick={item.onClick}
                  style={{
                    '--x': `${x}px`,
                    '--y': `${y}px`,
                    '--delay': `${i * 0.07}s`,
                  }}
                >
                  <img
                    src={authUserProfile?.avatar_url || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K"}
                    alt={authUserProfile?.display_name || 'User'}
                    className="menu-item-avatar-img"
                  />
                  <span className="floating-menu-item-label">{item.label}</span>
                </button>
              );
            }

            return (
              <button
                key={`item-${i}`}
                className={`floating-menu-item ${item.className} ${isActive ? 'active' : ''}`}
                onClick={item.onClick}
                style={{
                  '--x': `${x}px`,
                  '--y': `${y}px`,
                  '--delay': `${i * 0.07}s`,
                }}
              >
                <div className="floating-menu-item-icon">
                  {item.icon}
                  {item.className?.includes('floating-menu-item-chat') && unreadChatConversationsCount > 0 && (
                    <span className="floating-chat-badge" aria-label={`Unread chats: ${unreadChatConversationsCount}`}>
                      {unreadChatConversationsCount}
                    </span>
                  )}
                </div>
                <span className="floating-menu-item-label">{item.label}</span>
              </button>
            );
          })}

          {/* === Inline "More" options (same row as the dots) === */}
          {showMore && (
            <>
              {/* Only show View and Filter buttons on /feed route */}
              {location.pathname === '/feed' && (
                <>
                  <button
                    className="more-inline-button"
                    style={{
                      '--mx': `${morePos.x + INLINE_GAP}px`,
                      '--my': `${morePos.y}px`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // TODO: hook this to view change action
                      console.log('View clicked');
                    }}
                    onMouseEnter={() => {
                      if (viewDropdownTimeoutRef.current) {
                        clearTimeout(viewDropdownTimeoutRef.current);
                      }
                      setIsViewDropdownFading(false);
                      setShowViewDropdown(true);
                    }}
                    onMouseLeave={() => {
                      viewDropdownTimeoutRef.current = setTimeout(() => {
                        setIsViewDropdownFading(true);
                        setTimeout(() => {
                          setShowViewDropdown(false);
                          setIsViewDropdownFading(false);
                        }, 200); // Animation duration
                      }, 300); // 300ms delay before closing
                    }}
                    aria-label={t.feed?.view || "View"}
                    title={t.feed?.view || "View"}
                  >
                    {selectedView === 'Detailed' && <FaTh />}
                    {selectedView === 'Grid' && (
                      <svg width="16" height="16" viewBox="0 0 320 512" fill="currentColor">
                        <path d="M96 32H32C14.33 32 0 46.33 0 64v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V64c0-17.67-14.33-32-32-32zm0 160H32c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zm0 160H32c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zM288 32h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V64c0-17.67-14.33-32-32-32zm0 160h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zm0 160h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32z"></path>
                      </svg>
                    )}
                    {selectedView === 'Masonry' && <FaThList />}

                    {/* View Dropdown */}
                    {showViewDropdown && (
                      <div
                        className={`view-dropdown ${isViewDropdownFading ? 'fading-out' : ''}`}
                        onMouseEnter={() => {
                          if (viewDropdownTimeoutRef.current) {
                            clearTimeout(viewDropdownTimeoutRef.current);
                          }
                          setIsViewDropdownFading(false);
                          setShowViewDropdown(true);
                        }}
                        onMouseLeave={() => {
                          viewDropdownTimeoutRef.current = setTimeout(() => {
                            setIsViewDropdownFading(true);
                            setTimeout(() => {
                              setShowViewDropdown(false);
                              setIsViewDropdownFading(false);
                            }, 200); // Animation duration
                          }, 300); // 300ms delay before closing
                        }}
                      >
                        <div className="view-header">{t.feed?.view || "View"}</div>
                        <div className="view-options">
                          <div
                            className={`view-option ${selectedView === 'Detailed' ? 'selected' : ''}`}
                            onClick={() => handleViewSelect('Detailed')}
                          >
                            <FaTh />
                            <span>{t.feed?.detailed || "Detailed"}</span>
                          </div>
                          <div
                            className={`view-option ${selectedView === 'Grid' ? 'selected' : ''}`}
                            onClick={() => handleViewSelect('Grid')}
                          >
                            <svg width="16" height="16" viewBox="0 0 320 512" fill="currentColor">
                              <path d="M96 32H32C14.33 32 0 46.33 0 64v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V64c0-17.67-14.33-32-32-32zm0 160H32c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zm0 160H32c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zM288 32h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V64c0-17.67-14.33-32-32-32zm0 160h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zm0 160h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32z"></path>
                            </svg>
                            <span>{t.feed?.grid || "Grid"}</span>
                          </div>
                          <div
                            className={`view-option ${selectedView === 'Masonry' ? 'selected' : ''}`}
                            onClick={() => handleViewSelect('Masonry')}
                          >
                            <FaThList />
                            <span>{t.feed?.masonry || "Masonry"}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </button>

                  <button
                    className="more-inline-button"
                    style={{
                      '--mx': `${morePos.x + INLINE_GAP * 2}px`,
                      '--my': `${morePos.y}px`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // TODO: hook this to your filters/settings action
                      console.log('Sliders clicked');
                    }}
                    onMouseEnter={() => {
                      if (filterDropdownTimeoutRef.current) {
                        clearTimeout(filterDropdownTimeoutRef.current);
                      }
                      setIsFilterDropdownFading(false);
                      setShowFilterDropdown(true);
                    }}
                    onMouseLeave={() => {
                      filterDropdownTimeoutRef.current = setTimeout(() => {
                        setIsFilterDropdownFading(true);
                        setTimeout(() => {
                          setShowFilterDropdown(false);
                          setIsFilterDropdownFading(false);
                        }, 200); // Animation duration
                      }, 300); // 300ms delay before closing
                    }}
                    aria-label={t.feed?.filters || "Filters"}
                    title={t.feed?.filters || "Filters"}
                  >
                    <FaSlidersH />

                    {/* Filter Dropdown */}
                    {showFilterDropdown && (
                      <div
                        className={`filter-dropdown ${isFilterDropdownFading ? 'fading-out' : ''}`}
                        onMouseEnter={() => {
                          if (filterDropdownTimeoutRef.current) {
                            clearTimeout(filterDropdownTimeoutRef.current);
                          }
                          setIsFilterDropdownFading(false);
                          setShowFilterDropdown(true);
                        }}
                        onMouseLeave={() => {
                          filterDropdownTimeoutRef.current = setTimeout(() => {
                            setIsFilterDropdownFading(true);
                            setTimeout(() => {
                              setShowFilterDropdown(false);
                              setIsFilterDropdownFading(false);
                            }, 200); // Animation duration
                          }, 300); // 300ms delay before closing
                        }}
                      >
                        <div className="filter-header">{t.feed?.filterByType || "Filter by type"}</div>
                        <div className="filter-options">
                          <div
                            className={`filter-option ${selectedFilter === 'All' ? 'selected' : ''}`}
                            onClick={() => handleFilterSelect('All')}
                          >
                            {t.feed?.all || "All"}
                          </div>
                          <div
                            className={`filter-option ${selectedFilter === 'Image' ? 'selected' : ''}`}
                            onClick={() => handleFilterSelect('Image')}
                          >
                            {t.feed?.image || "Image"}
                          </div>
                          <div
                            className={`filter-option ${selectedFilter === 'Videos' ? 'selected' : ''}`}
                            onClick={() => handleFilterSelect('Videos')}
                          >
                            {t.feed?.videos || "Videos"}
                          </div>
                          <div
                            className={`filter-option ${selectedFilter === 'Links' ? 'selected' : ''}`}
                            onClick={() => handleFilterSelect('Links')}
                          >
                            {t.feed?.links || "Links"}
                          </div>
                          {/* <div 
                            className={`filter-option ${selectedFilter === 'Audio' ? 'selected' : ''}`}
                            onClick={() => handleFilterSelect('Audio')}
                          >
                            {t.feed?.audio || "Audio"}
                          </div>
                          <div 
                            className={`filter-option ${selectedFilter === 'Locked' ? 'selected' : ''}`}
                            onClick={() => handleFilterSelect('Locked')}
                          >
                            {t.feed?.locked || "Locked"}
                          </div> */}
                        </div>
                      </div>
                    )}
                  </button>
                </>
              )}

              {/* Navigation button - always show but adjust position based on whether view/filter buttons are shown */}
              <button
                className="more-inline-button"
                style={{
                  '--mx': `${morePos.x + INLINE_GAP * (location.pathname === '/feed' ? 3 : 1)}px`,
                  '--my': `${morePos.y}px`,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Navigation clicked');
                }}
                onMouseEnter={() => {
                  if (navigationDropdownTimeoutRef.current) {
                    clearTimeout(navigationDropdownTimeoutRef.current);
                  }
                  setIsNavigationDropdownFading(false);
                  setShowNavigationDropdown(true);
                }}
                onMouseLeave={() => {
                  navigationDropdownTimeoutRef.current = setTimeout(() => {
                    setIsNavigationDropdownFading(true);
                    setTimeout(() => {
                      setShowNavigationDropdown(false);
                      setIsNavigationDropdownFading(false);
                    }, 200); // Animation duration
                  }, 300); // 300ms delay before closing
                }}
                aria-label={t.feed?.navigation || "Navigation"}
                title={t.feed?.navigation || "Navigation"}
              >
                <FaCompass />

                {/* Navigation Dropdown */}
                {showNavigationDropdown && (
                  <div
                    className={`navigation-dropdown ${isNavigationDropdownFading ? 'fading-out' : ''}`}
                    onMouseEnter={() => {
                      if (navigationDropdownTimeoutRef.current) {
                        clearTimeout(navigationDropdownTimeoutRef.current);
                      }
                      setIsNavigationDropdownFading(false);
                      setShowNavigationDropdown(true);
                    }}
                    onMouseLeave={() => {
                      navigationDropdownTimeoutRef.current = setTimeout(() => {
                        setIsNavigationDropdownFading(true);
                        setTimeout(() => {
                          setShowNavigationDropdown(false);
                          setIsNavigationDropdownFading(false);
                        }, 200); // Animation duration
                      }, 300); // 300ms delay before closing
                    }}
                  >
                    <div className="navigation-header">{t.feed?.navigation || "Navigation"}</div>
                    <div className="navigation-options">
                      <div
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/')}
                      >
                        {t.feed?.authentication || "Authentication"}
                      </div>
                      <div
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/about')}
                      >
                        {t.feed?.aboutUs || "About Us"}
                      </div>
                      <div
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/contact')}
                      >
                        {t.feed?.contactUs || "Contact Us"}
                      </div>
                      <div
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/faq')}
                      >
                        {t.feed?.faq || "FAQ"}
                      </div>
                      <div
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/privacy-policy')}
                      >
                        {t.feed?.privacyPolicy || "Privacy Policy"}
                      </div>
                      <div
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/terms-of-services')}
                      >
                        {t.feed?.termsOfServices || "Terms of Services"}
                      </div>
                      {/* <div 
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/buy-winis')}
                      >
                        Buy winis
                      </div> */}
                      {/* <div 
                        className="navigation-option"
                        onClick={() => handleNavigationSelect('/investment')}
                      >
                        {t.feed?.investment || "Investment"}
                      </div> */}
                    </div>
                  </div>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloatingSidebarButton;
